import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Building2,
  LogOut,
  Menu,
  X,
  Home,
  Shield,
  Users,
  UserCircle,
  Calendar,
  FileText,
  Crown,
} from "lucide-react";
import { marcaDe } from '../utils/marca';
import useAuthStore from "../store/useAuthStore";
import ThemeToggle from "./ThemeToggle";
import NotificationBell from "./NotificationBell";
import NotificationBellHeader from "./NotificationBellHeader";
import SelectorResidencial from "./SelectorResidencial";

export default function Header() {
  const { user, logout } = useAuthStore();
  // El nombre y el logo salen de la residencial de quien entró, no del código.
  const marca = marcaDe(user);
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [hoveredLink, setHoveredLink] = useState(null);
  const [hoveredLogo, setHoveredLogo] = useState(false);
  const [logoutHovered, setLogoutHovered] = useState(false);
  const [mobileButtonHovered, setMobileButtonHovered] = useState(false);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);

  // Update window width on resize
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location]);

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [mobileMenuOpen]);

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const navItems = {
    superadmin: [
      { path: "/residenciales", label: "Residenciales", icon: Building2 },
      { path: "/admin", label: "Dashboard", icon: Home },
    ],
    admin: [{ path: "/admin", label: "Dashboard", icon: Home }],
    guard: [{ path: "/guard", label: "Panel de Control", icon: Shield }],
    resident: [{ path: "/resident", label: "Mis Visitas", icon: Home }],
  };

  const currentNavItems = navItems[user?.role] || [];

  const roleIcons = {
    superadmin: Crown,
    admin: Crown,
    guard: Shield,
    resident: UserCircle,
  };

  const roleLabels = {
    superadmin: "Plataforma",
    admin: "Administrador",
    guard: "Guardia",
    resident: "Residente",
  };

  const RoleIcon = roleIcons[user?.role] || UserCircle;

  const isMobile = windowWidth < 768;
  const isTablet = windowWidth >= 768 && windowWidth < 1024;
  const isDesktop = windowWidth >= 1024;

  const styles = {
    header: {
      background: "var(--bg-elevated)",
      borderBottom: "2px solid var(--gold)",
      position: "sticky",
      top: 0,
      zIndex: 50,
      backdropFilter: "blur(20px)",
      WebkitBackdropFilter: "blur(20px)",
      boxShadow: "var(--shadow-xl)",
    },
    container: {
      width: "100%",
      maxWidth: "1400px",
      margin: "0 auto",
      padding: "0 var(--container-padding)",
    },
    nav: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      minHeight: isMobile ? "60px" : "80px",
      /* `gap` y `minWidth: 0` para que los dos lados puedan encoger. Sin el
         `minWidth`, un hijo flex nunca baja de su contenido y empuja la
         cabecera más allá de la pantalla. */
      gap: "var(--space-2)",
      minWidth: 0,
    },
    logo: {
      display: "flex",
      alignItems: "center",
      gap: isMobile ? "0.75rem" : "1rem",
      textDecoration: "none",
      color: "var(--text)",
      transition: "transform 0.3s ease",
    },
    logoHover: {
      transform: "scale(1.05)",
    },
    logoIcon: {
      width: isMobile ? "40px" : "50px",
      height: isMobile ? "40px" : "50px",
      background: "var(--gradient-gold)",
      borderRadius: isMobile ? "var(--radius-lg)" : "var(--radius-xl)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      color: "white",
      flexShrink: 0,
      boxShadow: "var(--shadow-gold)",
      position: "relative",
      overflow: "hidden",
    },
    logoIconShimmer: {
      position: "absolute",
      top: 0,
      left: "-100%",
      width: "100%",
      height: "100%",
      background:
        "linear-gradient(90deg, transparent 0%, rgba(255, 255, 255, 0.4) 50%, transparent 100%)",
      animation: "shimmer 3s infinite",
    },
    logoText: {
      fontFamily: "var(--font-display)",
      fontSize: windowWidth < 640 ? "var(--text-lg)" : "var(--text-xl)",
      fontWeight: "700",
      letterSpacing: "var(--tracking-tight)",
      display: windowWidth < 640 ? "none" : "block",
    },
    centerSection: {
      display: isMobile ? "none" : "flex",
      alignItems: "center",
      gap: "var(--space-3)",
      flex: 1,
      justifyContent: "center",
    },
    navLinks: {
      display: "flex",
      alignItems: "center",
      gap: isDesktop ? "var(--space-3)" : "var(--space-2)",
    },
    navLink: {
      display: "flex",
      alignItems: "center",
      gap: "0.5rem",
      padding: isDesktop ? "0.75rem 1.5rem" : "0.5rem 1rem",
      borderRadius: "var(--radius-lg)",
      color: "var(--text-secondary)",
      textDecoration: "none",
      fontSize: "var(--text-sm)",
      fontFamily: "var(--font-body)",
      fontWeight: "500",
      letterSpacing: "var(--tracking-wide)",
      textTransform: "uppercase",
      transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
      cursor: "pointer",
      position: "relative",
      whiteSpace: "nowrap",
    },
    navLinkActive: {
      background: "var(--gold-subtle)",
      color: "var(--gold-dark)",
      fontWeight: "600",
    },
    navLinkHover: {
      background: "var(--midnight-10)",
      color: "var(--gold)",
      transform: "translateY(-2px)",
    },
    navLinkActiveBar: {
      position: "absolute",
      bottom: "-2px",
      left: "50%",
      transform: "translateX(-50%)",
      width: "80%",
      height: "3px",
      background: "var(--gradient-gold)",
      borderRadius: "var(--radius-full)",
    },
    rightSection: {
      display: "flex",
      alignItems: "center",
      gap: isDesktop ? "var(--space-4)" : "var(--space-2)",
      /* Encoge antes de desbordar: a 768 px este grupo medía 404 px fijos
         —selector, campana, tema y salir— y sacaba la cabecera 13 px fuera. */
      minWidth: 0,
      flexShrink: 1,
    },
    userInfo: {
      display: isDesktop ? "flex" : "none",
      flexDirection: "column",
      alignItems: "flex-end",
      gap: "0.25rem",
    },
    userName: {
      fontFamily: "var(--font-body)",
      fontSize: "var(--text-base)",
      fontWeight: "600",
      color: "var(--text)",
      letterSpacing: "var(--tracking-wide)",
    },
    userRole: {
      fontSize: "var(--text-xs)",
      color: "var(--text-tertiary)",
      textTransform: "capitalize",
    },
    actionButtons: {
      /* En teléfono NO se dibuja: la campana, el tema y el salir ya están en el
         cajón del menú, y dibujarlos además aquí sacaba la cabecera fuera de la
         pantalla —el grupo medía 404 px fijos y no encogía—. El selector de
         residencial se movió al cajón por lo mismo. */
      display: isDesktop ? "flex" : "none",
      alignItems: "center",
      gap: windowWidth < 640 ? "var(--space-2)" : "var(--space-3)",
    },
    logoutButton: {
      display: "flex",
      alignItems: "center",
      gap: "0.5rem",
      padding: isMobile
        ? "0.5rem"
        : isTablet
        ? "0.5rem 1rem"
        : "0.75rem 1.5rem",
      borderRadius: "var(--radius-lg)",
      border: "2px solid var(--danger)",
      backgroundColor: "transparent",
      color: "var(--danger)",
      fontSize: "var(--text-sm)",
      fontFamily: "var(--font-body)",
      fontWeight: "600",
      letterSpacing: "var(--tracking-wide)",
      textTransform: "uppercase",
      cursor: "pointer",
      transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
    },
    logoutButtonText: {
      display: windowWidth < 640 ? "none" : "inline",
    },
    logoutButtonHover: {
      backgroundColor: "var(--danger)",
      color: "white",
      transform: "translateY(-2px)",
      boxShadow: "0 8px 32px rgba(198, 40, 40, 0.3)",
    },
    mobileMenuButton: {
      /* Hasta escritorio, inclusive la tableta: es el complemento exacto de
         `actionButtons`, que solo aparece en escritorio. Antes el hamburguesa
         se iba a 768 px y las acciones de escritorio entraban ahí mismo, sin
         caber: la cabecera se salía 128 px. */
      display: isDesktop ? "none" : "flex",
      alignItems: "center",
      justifyContent: "center",
      padding: "0.5rem",
      borderRadius: "var(--radius-lg)",
      border: "2px solid var(--gold)",
      backgroundColor: "transparent",
      color: "var(--gold)",
      cursor: "pointer",
      transition: "all 0.3s ease",
    },
    mobileMenuButtonHover: {
      backgroundColor: "var(--gold)",
      color: "white",
    },
    mobileMenuOverlay: {
      position: "fixed",
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: "rgba(0, 0, 0, 0.5)",
      zIndex: 40,
      display: mobileMenuOpen && !isDesktop ? "block" : "none",
    },
    mobileMenu: {
      position: "fixed",
      top: 0,
      right: 0,
      bottom: 0,
      width: "100%",
      maxWidth: "320px",
      backgroundColor: "var(--bg-elevated)",
      boxShadow: "var(--shadow-2xl)",
      display: !isDesktop ? "flex" : "none",
      flexDirection: "column",
      zIndex: 50,
      transform: mobileMenuOpen ? "translateX(0)" : "translateX(100%)",
      transition: "transform 0.3s ease-in-out",
    },
    mobileMenuHeader: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      padding: "var(--space-4)",
      borderBottom: "1px solid var(--border)",
    },
    mobileMenuContent: {
      flex: 1,
      overflowY: "auto",
      padding: "var(--space-4)",
    },
    mobileMenuSection: {
      marginBottom: "var(--space-6)",
    },
    mobileMenuSectionTitle: {
      fontSize: "var(--text-xs)",
      fontWeight: "600",
      textTransform: "uppercase",
      letterSpacing: "var(--tracking-widest)",
      color: "var(--text-tertiary)",
      marginBottom: "var(--space-3)",
    },
    mobileNavLink: {
      display: "flex",
      alignItems: "center",
      gap: "var(--space-3)",
      padding: "var(--space-3) var(--space-4)",
      marginBottom: "var(--space-2)",
      borderRadius: "var(--radius-lg)",
      color: "var(--text)",
      textDecoration: "none",
      fontSize: "var(--text-base)",
      fontFamily: "var(--font-body)",
      fontWeight: "500",
      transition: "all 0.2s ease",
    },
    mobileNavLinkActive: {
      background: "var(--gold-subtle)",
      color: "var(--gold-dark)",
      fontWeight: "600",
    },
    mobileMenuFooter: {
      padding: "var(--space-4)",
      borderTop: "1px solid var(--border)",
    },
    roleBadge: {
      display: "inline-flex",
      alignItems: "center",
      gap: "0.5rem",
      padding: "0.375rem 1rem",
      borderRadius: "var(--radius-full)",
      fontSize: "var(--text-xs)",
      fontFamily: "var(--font-body)",
      fontWeight: "600",
      letterSpacing: "var(--tracking-widest)",
      textTransform: "uppercase",
      background: "var(--gradient-gold)",
      color: "white",
      boxShadow: "var(--shadow-sm)",
    },
  };

  return (
    <>
      <header style={styles.header}>
        <div style={styles.container}>
          <nav style={styles.nav}>
            <a
              href="/"
              style={{
                ...styles.logo,
                ...(hoveredLogo ? styles.logoHover : {}),
              }}
              onMouseEnter={() => setHoveredLogo(true)}
              onMouseLeave={() => setHoveredLogo(false)}
            >
              <div style={styles.logoIcon}>
                <Building2 size={24} />
                <div style={styles.logoIconShimmer} />
              </div>
              <span style={styles.logoText}>{marca.name}</span>
            </a>

            <div style={styles.centerSection}>
              <div style={styles.navLinks}>
                {currentNavItems.map((item) => {
                  const Icon = item.icon;
                  const isActive = location.pathname === item.path;
                  const isHovered = hoveredLink === item.path;

                  return (
                    <a
                      key={item.path}
                      href={item.path}
                      style={{
                        ...styles.navLink,
                        ...(isActive ? styles.navLinkActive : {}),
                        ...(isHovered && !isActive ? styles.navLinkHover : {}),
                      }}
                      onMouseEnter={() => setHoveredLink(item.path)}
                      onMouseLeave={() => setHoveredLink(null)}
                      onClick={(e) => {
                        e.preventDefault();
                        navigate(item.path);
                      }}
                    >
                      <Icon size={18} />
                      {item.label}
                      {isActive && <div style={styles.navLinkActiveBar} />}
                    </a>
                  );
                })}
              </div>
            </div>

            <div style={styles.rightSection}>
              <div style={styles.userInfo}>
                <span style={styles.userName}>{user?.name}</span>
                <div style={styles.roleBadge}>
                  <RoleIcon size={14} />
                  <span>{roleLabels[user?.role] || user?.role}</span>
                </div>
              </div>

              <div style={styles.actionButtons}>
                {/* Solo se dibuja para el superadministrador. */}
                <SelectorResidencial />
                <NotificationBellHeader />
                <ThemeToggle variant="minimal" showLabel={false} />
                <button
                  onClick={handleLogout}
                  style={{
                    ...styles.logoutButton,
                    ...(logoutHovered ? styles.logoutButtonHover : {}),
                  }}
                  onMouseEnter={() => setLogoutHovered(true)}
                  onMouseLeave={() => setLogoutHovered(false)}
                >
                  <LogOut size={18} />
                  <span style={styles.logoutButtonText}>Salir</span>
                </button>
              </div>

              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                onMouseEnter={() => setMobileButtonHovered(true)}
                onMouseLeave={() => setMobileButtonHovered(false)}
                style={{
                  ...styles.mobileMenuButton,
                  ...(mobileButtonHovered ? styles.mobileMenuButtonHover : {}),
                }}
                aria-label="Toggle mobile menu"
              >
                {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
              </button>
            </div>
          </nav>
        </div>
      </header>

      {/* El cajón del menú móvil SOLO existe mientras está abierto.
          Antes vivía siempre en el DOM, fuera de la pantalla, con
          `transform: translateX(100%)`. Y un elemento `position: fixed` NO lo
          recorta el `overflow: hidden` de ningún ancestro —se posiciona contra
          la ventana, no contra el contenedor—, así que seguía sumando ancho al
          documento y aparecía una barra de desplazamiento horizontal en
          teléfono y en tableta, en todas las pantallas de todos los roles.
          Se pierde la animación de salida; la de entrada se conserva. */}
      {mobileMenuOpen && !isDesktop && (
        <div
          style={styles.mobileMenuOverlay}
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      {mobileMenuOpen && !isDesktop && (
      <div style={styles.mobileMenu}>
        <div style={styles.mobileMenuHeader}>
          <div>
            <div style={styles.userName}>{user?.name}</div>
            <div style={{ ...styles.roleBadge, marginTop: "var(--space-2)" }}>
              <RoleIcon size={14} />
              <span>{roleLabels[user?.role] || user?.role}</span>
            </div>
            {/* Sólo se dibuja para el superadministrador, y aquí porque en la
                cabecera no cabe en un teléfono. */}
            <div style={{ marginTop: "var(--space-3)" }}>
              <SelectorResidencial />
            </div>
          </div>
          <button
            onClick={() => setMobileMenuOpen(false)}
            style={{
              ...styles.mobileMenuButton,
              display: "flex",
              border: "none",
              padding: "var(--space-2)",
            }}
            aria-label="Close mobile menu"
          >
            <X size={24} />
          </button>
        </div>

        <div style={styles.mobileMenuContent}>
          <div style={styles.mobileMenuSection}>
            <div style={styles.mobileMenuSectionTitle}>Navegación</div>
            {currentNavItems.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;

              return (
                <a
                  key={item.path}
                  href={item.path}
                  style={{
                    ...styles.mobileNavLink,
                    ...(isActive ? styles.mobileNavLinkActive : {}),
                  }}
                  onClick={(e) => {
                    e.preventDefault();
                    navigate(item.path);
                    setMobileMenuOpen(false);
                  }}
                >
                  <Icon size={20} />
                  {item.label}
                </a>
              );
            })}
          </div>

          <div style={styles.mobileMenuSection}>
            <div style={styles.mobileMenuSectionTitle}>Configuración</div>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "var(--space-3) 0",
              }}
            >
              <span style={{ fontSize: "var(--text-base)" }}>
                Notificaciones
              </span>
              <NotificationBell />
            </div>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "var(--space-3) 0",
              }}
            >
              <span style={{ fontSize: "var(--text-base)" }}>Tema</span>
              <ThemeToggle variant="minimal" showLabel={false} />
            </div>
          </div>
        </div>

        <div style={styles.mobileMenuFooter}>
          <button
            onClick={handleLogout}
            style={{
              ...styles.logoutButton,
              width: "100%",
              justifyContent: "center",
              padding: "var(--space-3)",
            }}
          >
            <LogOut size={18} />
            <span>Cerrar Sesión</span>
          </button>
        </div>
      </div>
      )}
    </>
  );
}
